import { getPlatformPreviewMeta } from "./postReviewWorkspace";

export const LIBRARY_TABS = [
  { id: "all", label: "All activity" },
  { id: "needs_review", label: "Needs review" },
  { id: "approved", label: "Approved" },
  { id: "scheduled", label: "Scheduled" },
  { id: "posts", label: "Posts" },
  { id: "supporting", label: "Supporting" },
  { id: "published", label: "Published" },
];

export const LIBRARY_LANES = [
  { id: "needs_review", label: "Needs review", hint: "Ready for a human decision" },
  { id: "approved", label: "Ready to schedule", hint: "Approved, not yet queued" },
  { id: "scheduled", label: "Scheduled", hint: "Queued on the calendar" },
  { id: "published", label: "Published", hint: "Finished records" },
];

const SUPPORTING_TYPES = new Set(["research_brief", "image_prompt", "execution_report"]);
const POST_TYPES = new Set(["social_post", "manual_post", "thread", "video_script", "carousel_outline", "imported_draft"]);
const PRIORITY = {
  needs_review: 0,
  draft: 1,
  approved: 2,
  scheduled: 3,
  publishing: 4,
  failed: 5,
  published: 6,
  archived: 7,
};

function normalizeDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
}

function matchesTab(asset, tab) {
  if (!tab || tab === "all") return true;
  if (tab === "posts") return POST_TYPES.has(asset.asset_type);
  if (tab === "supporting") return SUPPORTING_TYPES.has(asset.asset_type);
  return asset.status === tab;
}

function getSearchText(asset) {
  return [
    asset.displayTitle,
    asset.sourcePrompt,
    asset.title,
    asset.hook,
    asset.body,
    asset.cta,
    asset.skill_used,
    asset.platform,
    asset.asset_type,
    Array.isArray(asset.hashtags) ? asset.hashtags.join(" ") : "",
  ].filter(Boolean).join(" ").toLowerCase();
}

function cleanTitle(value = "", limit = 96) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (text.length <= limit) return text;
  return `${text.slice(0, Math.max(0, limit - 3)).trimEnd()}...`;
}

function sentenceLead(value = "") {
  return String(value || "").split(/[.!?\n]/)[0]?.trim() || "";
}

function titleCaseLead(value = "") {
  const cleaned = cleanTitle(value);
  if (!cleaned) return "";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

// Strip trailing authoring instructions ("Reference...", "Use a bulleted list",
// "Include a CTA", "no CTA", etc.) that aren't part of the subject.
function stripInstructionTail(value = "") {
  return String(value || "")
    .replace(
      /[\s.,;:-]+(?:reference|use|include|includes|including|end with|start with|starting with|begin with|provide|make sure|ensure|do not|don't|add|keep|format|formatted|with a cta|no\s+cta|and\s+(?:include|use|end|add|provide)).*$/i,
      "",
    )
    .replace(/^[\s.,:;-]+|[\s.,:;-]+$/g, "");
}

export function deriveLibrarySubject(prompt = "", fallback = "") {
  const firstSentence = sentenceLead(prompt);

  // 1) Explicitly-stated topics ("...top 5 X", "...about/on X").
  const patterns = [
    /\b(?:listing|list)\s+(?:the\s+)?(?<topic>top\s+\d+\s+.+)$/i,
    /\b(?:about|on|regarding|covering)\s+(?<topic>.+)$/i,
  ];
  for (const pattern of patterns) {
    const topic = stripInstructionTail(firstSentence.match(pattern)?.groups?.topic || "");
    if (topic) return titleCaseLead(topic);
  }

  // 2) Command-style prompts ("Write a highly detailed post describing the
  //    necessity of implementing strong cyber security into nuclear reactors")
  //    — peel off the command clause + filler lead to reach the real subject.
  let text = firstSentence
    // "Write a [adjectives] [platform] post/thread/... [describing|about|of] "
    .replace(
      /^\s*(?:please\s+)?(?:write|create|make|draft|generate|compose|produce|craft|build)\b[^]*?\b(?:post|caption|thread|article|carousel|reel|short|script|blog|blurb|piece)\b\s*(?:that\s+)?(?:describing|describes|about|on|regarding|explaining|explains|covering|covers|detailing|discussing|highlighting|outlining|of|for)?\s*/i,
      "",
    )
    // filler leads: "the necessity of", "the importance of", "the benefits of"...
    .replace(
      /^(?:the\s+)?(?:necessity|importance|need|benefits?|value|role|impact|power|rise|future|basics|fundamentals|advantages|reasons|case)\s+(?:of|for|to|why)\s+/i,
      "",
    );
  text = stripInstructionTail(text);
  if (text && text.toLowerCase() !== firstSentence.toLowerCase()) return titleCaseLead(text);

  return titleCaseLead(stripInstructionTail(firstSentence) || fallback);
}

function buildRunByAssetId(runs = []) {
  const byAssetId = new Map();
  (runs || []).forEach((run) => {
    (run.asset_ids || []).forEach((assetId) => {
      if (assetId && !byAssetId.has(assetId)) byAssetId.set(assetId, run);
    });
  });
  return byAssetId;
}

function enrichAssetWithRun(asset, runByAssetId) {
  const run = runByAssetId.get(asset.id);
  const sourcePrompt = run?.prompt || asset.sourcePrompt || "";
  const displayTitle = deriveLibrarySubject(sourcePrompt, asset.title) || asset.title;
  return {
    ...asset,
    sourcePrompt,
    sourceRunId: run?.id,
    displayTitle,
  };
}

export function getLibraryAssetAction(asset = {}) {
  switch (asset.status) {
    case "needs_review":
    case "draft":
      return {
        label: "Review",
        tone: "review",
        hint: "Needs your decision before scheduling.",
      };
    case "approved":
      return {
        label: "Schedule",
        tone: "approved",
        hint: "Approved and ready for the calendar.",
      };
    case "scheduled":
      return {
        label: "Calendar",
        tone: "scheduled",
        hint: "Queued on the publishing calendar.",
      };
    case "published":
      return {
        label: "Record",
        tone: "published",
        hint: "Published and saved to history.",
      };
    case "archived":
      return {
        label: "Archived",
        tone: "archived",
        hint: "Out of the active queue.",
      };
    default:
      return {
        label: "Open",
        tone: "neutral",
        hint: "Open the asset workspace.",
      };
  }
}

export function filterLibraryAssets(assets = [], filters = {}) {
  const tab = filters.tab || "all";
  const platform = filters.platform || "all";
  const status = filters.status || "all";
  const query = (filters.query || "").trim().toLowerCase();

  return assets
    .filter((asset) => matchesTab(asset, tab))
    .filter((asset) => platform === "all" || asset.platform === platform)
    .filter((asset) => status === "all" || asset.status === status)
    .filter((asset) => !query || getSearchText(asset).includes(query))
    .sort((a, b) => {
      const supportingWeight = Number(SUPPORTING_TYPES.has(a.asset_type)) - Number(SUPPORTING_TYPES.has(b.asset_type));
      if (supportingWeight !== 0) return supportingWeight;
      const priority = (PRIORITY[a.status] ?? 8) - (PRIORITY[b.status] ?? 8);
      if (priority !== 0) return priority;
      return normalizeDate(b.updated_at) - normalizeDate(a.updated_at);
    });
}

export function buildLibraryWorkspace({ assets = [], runs = [], filters = {} } = {}) {
  const runByAssetId = buildRunByAssetId(runs);
  const enrichedAssets = assets.map((asset) => enrichAssetWithRun(asset, runByAssetId));
  const summary = {
    total: enrichedAssets.length,
    needsReview: enrichedAssets.filter((asset) => asset.status === "needs_review").length,
    readyToSchedule: enrichedAssets.filter((asset) => asset.status === "approved").length,
    scheduled: enrichedAssets.filter((asset) => asset.status === "scheduled").length,
    published: enrichedAssets.filter((asset) => asset.status === "published").length,
    supporting: enrichedAssets.filter((asset) => SUPPORTING_TYPES.has(asset.asset_type)).length,
  };

  // Counts live on the tabs (stats-as-filters) — no separate stat-card row.
  const tabs = LIBRARY_TABS.map((tab) => ({
    ...tab,
    count: enrichedAssets.filter((asset) => matchesTab(asset, tab.id)).length,
  }));

  const filtered = filterLibraryAssets(enrichedAssets, filters).map((asset) => ({
    ...asset,
    libraryAction: getLibraryAssetAction(asset),
    previewMeta: getPlatformPreviewMeta(asset),
    isSupporting: SUPPORTING_TYPES.has(asset.asset_type),
  }));

  return {
    summary,
    tabs,
    filtered,
  };
}
